'use client';

import { useState } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';

import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { Button } from '@eliadmin/core/src/components/ui/button';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

export const ProductsList = ({
  categories,
  locale,
}: {
  categories: CategoriesEntity[];
  locale: string;
}) => {
  const { t } = useTranslations();
  const { me, isInitialized } = useCustomerStore();
  const [activeKey, setActiveKey] = useState<string>('all'); // selected node id (parent or child)
  const [activeParentKey, setActiveParentKey] = useState<string>('all'); // highlighted parent id

  const findCategoryAndParentById = (
    nodes: CategoriesEntity[] = [],
    id: number,
    parent?: CategoriesEntity,
  ): { node: CategoriesEntity; parent?: CategoriesEntity } | undefined => {
    for (const node of nodes) {
      if (node.id === id) return { node, parent };
      const inChildren = findCategoryAndParentById(
        (node.childs as CategoriesEntity[] | undefined) ?? [],
        id,
        node,
      );
      if (inChildren) return inChildren;
    }
    return undefined;
  };

  const activeNodeInfo =
    activeKey === 'all' ? undefined : findCategoryAndParentById(categories, parseInt(activeKey));
  const activeParentCategory: CategoriesEntity | undefined = activeNodeInfo
    ? (activeNodeInfo.parent ?? activeNodeInfo.node)
    : undefined;

  const activeTitleText =
    activeKey === 'all'
      ? 'Všetky produkty'
      : activeNodeInfo?.parent
        ? `${activeNodeInfo.parent.name} / ${activeNodeInfo.node.name}`
        : (activeNodeInfo?.node?.name ?? '');
  const activeIdForQuery = activeNodeInfo?.node?.id;

  const customQuery = buildProductsQuery({
    categories: activeKey === 'all' ? [] : [activeIdForQuery ?? 0],
  });

  const productsQuery = useInfiniteQuery({
    queryKey: [CacheKey.PRODUCTS, activeKey, locale, me?.customer.id ?? null],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchProducts({
        locale,
        authToken: me?.authToken,
        query: {
          ...customQuery,
          page: pageParam,
          results: 8,
        },
      });
    },
    getNextPageParam: (lastPage: any, _pages) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
    enabled: isInitialized,
  });

  const products = productsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  const handleTabChange = (value: string) => {
    if (value === 'all') {
      setActiveParentKey('all');
      setActiveKey('all');
      return;
    }
    const info = findCategoryAndParentById(categories, parseInt(value));
    if (info) {
      setActiveKey(String(info.node.id));
      setActiveParentKey(String(info.parent?.id ?? info.node.id));
    } else {
      setActiveKey(value);
      setActiveParentKey(value);
    }
  };

  const handleChildToggle = (childId: string) => {
    if (activeKey === childId) {
      // unselect child -> keep/highlight parent
      setActiveKey(activeParentKey);
      return;
    }
    handleTabChange(childId);
  };

  return (
    <>
      <section className={cn('ea-products-list__section')}>
        <Tabs
          className={cn('ea-products-list__tabs')}
          value={activeParentKey}
          defaultValue={activeParentKey}
          onValueChange={handleTabChange}
        >
          <TabsList className={cn('ea-products-list__tabs-list')}>
            <TabsTrigger
              value={'all'}
              className={cn(
                'ea-products-list__tabs-trigger',
                'data-[state=active]:bg-primary data-[state=active]:text-primary-foreground',
              )}
            >
              {t('text494')}
            </TabsTrigger>
            {categories.map(category => (
              <TabsTrigger
                value={String(category.id)}
                key={`parent-${category.id}`}
                className={cn(
                  'ea-products-list__tabs-trigger',
                  'data-[state=active]:bg-primary data-[state=active]:text-primary-foreground',
                )}
              >
                {category.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {activeParentCategory?.childs?.length ? (
            <div
              className={cn('mt-2 hidden flex-wrap gap-2 md:flex', 'ea-products-list__child-tabs')}
            >
              {(activeParentCategory.childs as CategoriesEntity[]).map(child => (
                <button
                  type="button"
                  key={`child-${child.id}`}
                  className={cn(
                    'h-fit w-fit rounded-full border border-primary px-4 py-4 text-xs font-semibold text-primary shadow-none',
                    activeKey === String(child.id)
                      ? 'bg-primary text-primary-foreground'
                      : 'opacity-80',
                  )}
                  onClick={() => handleChildToggle(String(child.id))}
                  aria-pressed={activeKey === String(child.id)}
                >
                  {child.name}
                </button>
              ))}
            </div>
          ) : null}
          <h2 className={cn('mb-8', 'ea-products-list__selected-category-title')}>
            {activeKey === 'all' && t('text494')}
            {activeKey !== 'all' && !activeNodeInfo?.parent && activeNodeInfo?.node?.name}
            {activeKey !== 'all' && activeNodeInfo?.parent && (
              <span className={cn('flex flex-wrap items-center gap-2')}>
                <button
                  type="button"
                  onClick={() => handleTabChange(String(activeNodeInfo.parent!.id))}
                  className={cn('text-primary hover:underline')}
                >
                  {activeNodeInfo.parent!.name}
                </button>
                <span>/</span>
                <span>{activeNodeInfo.node.name}</span>
              </span>
            )}
          </h2>
          <TabsContent value={activeParentKey} className={cn('ea-products-list__tabs-content')}>
            {productsQuery.isLoading ? (
              <ProductsSkeleton />
            ) : (
              <>
                {products.length > 0 ? (
                  products.map(product => <ProductCard product={product} key={product?.name} />)
                ) : (
                  <div className={cn('col-span-1 text-center text-sm text-gray-500 lg:col-span-2')}>
                    V tejto kategórii sa zatiaľ nenachádzajú žiadne produkty.
                  </div>
                )}
              </>
            )}
            {productsQuery.error && (
              <div className={cn('text-center text-sm text-gray-500')}>
                Nastala chyba pri načítaní produktov.
              </div>
            )}
          </TabsContent>
        </Tabs>
        <div className={cn('', 'ea-products-list__mobile-container')}>
          <Select
            onValueChange={value => {
              // selecting parent from dropdown
              handleTabChange(value);
            }}
            value={activeParentKey}
          >
            <SelectTrigger aria-label={`Kategória: ${activeTitleText}`} className={cn('', 'ea-products-list__select-trigger')}>
              <SelectValue placeholder={activeTitleText} />
            </SelectTrigger>
            <SelectContent className={cn('', 'ea-products-list__select-content')}>
              <SelectItem value={'all'} className={cn('', 'ea-products-list__select-item')}>
                {t('text494')}
              </SelectItem>
              {categories?.map(category => (
                <SelectItem value={String(category?.id)} key={`sel-parent-${category.id}`}>
                  {category?.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeParentCategory?.childs?.length ? (
            <div
              className={cn('mt-4 flex flex-wrap gap-2 md:hidden', 'ea-products-list__child-tabs')}
            >
              {(activeParentCategory.childs as CategoriesEntity[]).map(child => (
                <button
                  type="button"
                  key={`mob-child-${child.id}`}
                  className={cn(
                    'h-fit w-fit rounded-full border border-primary px-3 py-2 text-xs font-semibold text-primary shadow-none',
                    activeKey === String(child.id)
                      ? 'bg-primary text-primary-foreground'
                      : 'opacity-80',
                  )}
                  onClick={() => handleChildToggle(String(child.id))}
                  aria-pressed={activeKey === String(child.id)}
                >
                  {child.name}
                </button>
              ))}
            </div>
          ) : null}
          <div className={cn('grid w-full grid-cols-1 gap-8')}>
            {productsQuery.isLoading ? (
              <ProductsSkeleton />
            ) : products.length > 0 ? (
              products.map(product => <ProductCard product={product} key={product?.name} />)
            ) : (
              <div className={cn('text-center text-sm text-gray-500')}>
                V tejto kategórii sa zatiaľ nenachádzajú žiadne produkty.
              </div>
            )}
          </div>
        </div>
      </section>
      <div
        className={cn('mb-24 flex w-full items-center justify-center', {
          hidden: !productsQuery.hasNextPage,
        })}
      >
        <Button
          onClick={() => productsQuery.fetchNextPage()}
          isLoading={productsQuery.isFetchingNextPage}
        >
          {t('text338')}
        </Button>
      </div>
    </>
  );
};

const ProductsSkeleton = () => {
  return (
    <div
      className={cn(
        'animate-pulse',
        'col-span-1 grid w-full grid-cols-1 gap-8 lg:col-span-2 lg:grid-cols-2',
      )}
    >
      <Skeleton className={cn('', 'col-span-1 h-[285px] bg-slate-100')} />
      <Skeleton className={cn('', 'col-span-1 h-[285px] bg-slate-100')} />
      <Skeleton className={cn('', 'col-span-1 h-[285px] bg-slate-100')} />
      <Skeleton className={cn('', 'col-span-1 h-[285px] bg-slate-100')} />
    </div>
  );
};
